import { Button } from '@up2stay/up2promo-ui';
import { Loader2, Sparkles } from 'lucide-react';

import type { ReviewsInbox } from '@/core/components/reputation/reviews/use-reviews-inbox';
import { useEntitlements } from '@/core/hooks/use-entitlements';

function Stat({
  label,
  value,
  danger = false,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <div>
      <div className="text-muted-foreground text-[11.5px]">{label}</div>
      <div
        className={
          danger
            ? 'text-destructive mt-0.5 text-[22px] font-bold tabular-nums'
            : 'mt-0.5 text-[22px] font-bold tabular-nums'
        }
      >
        {value}
      </div>
    </div>
  );
}

function formatResponseTime(
  hours: number | null,
  t: ReviewsInbox['reviewsDict']['stats'],
): string {
  if (hours == null) return '–';
  if (hours < 1)
    return t.minutes.replace(
      '{n}',
      String(Math.max(1, Math.round(hours * 60))),
    );
  if (hours < 48) return t.hours.replace('{n}', String(Math.round(hours)));
  return t.days.replace('{n}', String(Math.round(hours / 24)));
}

/** Inbox stat strip (unanswered / critical / avg response time) + bulk drafting. */
export function ReviewsInboxHeader({
  inbox,
  mode,
}: {
  inbox: ReviewsInbox;
  /** `header`: stats + button in the desktop page header. `inline`: mobile-only button in the body. */
  mode: 'header' | 'inline';
}) {
  const t = inbox.reviewsDict.stats;
  const { stats } = inbox;
  const locale = inbox.locale;
  const { isFeatureEnabled } = useEntitlements();
  const aiEnabled = isFeatureEnabled('feature.reputation_ai');
  return (
    <div
      className={
        mode === 'header'
          ? 'hidden items-center gap-6 md:flex'
          : 'flex md:hidden'
      }
    >
      {mode === 'header' && stats.scoped ? (
        <>
          <Stat
            label={t.unanswered}
            value={stats.unanswered.toLocaleString(locale)}
          />
          <span
            className="hidden h-[34px] w-px bg-border sm:block"
            aria-hidden
          />
          <Stat
            label={t.critical}
            value={stats.critical.toLocaleString(locale)}
            danger={stats.critical > 0}
          />
          <span
            className="hidden h-[34px] w-px bg-border sm:block"
            aria-hidden
          />
          <Stat
            label={t.avgResponse}
            value={formatResponseTime(stats.averageResponseHours, t)}
          />
        </>
      ) : null}
      {aiEnabled && inbox.canReply ? (
        <Button
          variant="outline"
          className={mode === 'inline' ? 'w-full' : undefined}
          disabled={inbox.draftAllPending || inbox.draftAllCount === 0}
          onClick={inbox.draftAll}
        >
          {inbox.draftAllPending ? (
            <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
          ) : (
            <Sparkles
              className="mr-2 size-4 text-[color:var(--ghost-accent-fg)]"
              aria-hidden
            />
          )}
          {inbox.draftAllPending ? t.drafting : t.draftAll}
        </Button>
      ) : null}
    </div>
  );
}
